import {
  Button,
  Checkbox,
  Flex,
  Grid,
  Image,
  Label,
  ModalManagerContext,
  Text,
  TextArea,
} from '@pega/cosmos-react-core';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { appState, confirmationAlertProps } from '../../../interfaces/common';

import { useTheme } from 'styled-components';
import * as Logos from '../../../constants/images';
import useLocalTranslation from '../../../hooks/useLocalTranslation';
import { fetchAllConfigurationRecords, updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import { tokenMaskerStyle } from '../../Utilities/custom.styled';
import { ConfirmationAlert } from '../../Utilities/CustomModal';
import MenuReplyConfiguration from '../../Utilities/MenuReplyConfiguration';
import SectionRenderTemplate from '../../Utilities/SectionRenderTemplate';
import { deleteAccount, getAccountDetails, updateMenuReplyConfiguration } from './TwilioWhatsappApis';
import CustomTooltip from '../../Utilities/CustomTooltip';
import RealTimeTranslation from '../Chat/Tabs/LanguageTab/RealTimeTranslation';

type AccountDetails = {
  accountSid: string;
  authToken: string;
  lastUpdate: string;
  name: string;
  phoneNumber: string;
  menuConfiguration?: string;
  hasTwilioWhatsAppTemplate?: boolean;
  messagingServiceSid?: string;
  translationServiceId: string;
  translateFromLanguages: string[];
  primaryLanguage: string;
  languageTranslationEnabled: boolean;
};

const TwilioWhatsapp: React.FC<any> = (props) => {
  const { id: connectionId }: any = useParams();
  const sessionId = useSelector((state: appState) => state.sessionId);
  const { configRecords } = useSelector((state: appState) => state);
  const initialState: AccountDetails = {
    accountSid: '',
    authToken: '',
    lastUpdate: '',
    name: '',
    phoneNumber: '',
    menuConfiguration: '',
    translationServiceId: '',
    translateFromLanguages: [],
    primaryLanguage: '',
    languageTranslationEnabled: false,
  };
  const [accountDetails, setAccountDetails] = useState(initialState);
  const {
    menuConfiguration = '{label} (TXT: {#})',
    hasTwilioWhatsAppTemplate = false,
    messagingServiceSid = '',
  } = accountDetails;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const [translationEnablerError, setTranslationEnablerError] = useState(false);
  const { create } = useContext(ModalManagerContext);
  const modalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const theme = useTheme();
  const deleteConfirmation = () => {
    modalMethods.current = create(
      ConfirmationAlert,
      {
        pageName: accountDetails?.name,
        deleteChannel: deleteChannel,
        ...props,
      },
      { alert: true },
    );
  };
  const [isMaskEnabled, setIsMaskEnabled] = useState(true);
  const [isTwilioTemplateEnabled, setIsTwilioTemplateEnabled] = useState(hasTwilioWhatsAppTemplate);
  const [serviceSid, setServiceSid] = useState(messagingServiceSid);
  const fetchAccountDetails = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await getAccountDetails(sessionId, connectionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      setAccountDetails(response?.data);
      const { hasTwilioWhatsAppTemplate = false, messagingServiceSid } = response?.data;
      setIsTwilioTemplateEnabled(hasTwilioWhatsAppTemplate);
      setServiceSid(messagingServiceSid);
    } else {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId, connectionId, navigate]);

  const deleteChannel = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await deleteAccount(connectionId, sessionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      navigate('/');
      dispatch(updateBannerInfo(`${translate('Twilio WhatsApp')} ${translate('account_deleted')}`, 'success'));
    } else if (response?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    } else {
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
  }, [sessionId, connectionId, dispatch, navigate, translate]);

  useEffect(() => {
    fetchAccountDetails();
  }, [fetchAccountDetails]);

  useEffect(() => {
    dispatch(fetchAllConfigurationRecords());
  }, [dispatch]);

  const getMaskedToken = (token: string = '') => {
    return !isMaskEnabled ? token : new Array(token.length).fill('*').join('');
  };

  const handleCheckboxClick = async (e: any) => {
    const checked = e.target.checked;
    setIsTwilioTemplateEnabled(checked);
    setServiceSid(messagingServiceSid);
    if (messagingServiceSid) {
      updateToTwilioTemplate(checked, messagingServiceSid);
    }
  };

  const updateToTwilioTemplate = async (checked: boolean, serviceSid: string) => {
    dispatch(updateLoaderDetails(true));
    const json = await updateMenuReplyConfiguration(sessionId, connectionId, menuConfiguration, checked, serviceSid);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(updateBannerInfo(`${translate('menu_config_update_success')}`, 'success'));
      setAccountDetails(json?.data);
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('menu_config_update_failure'), 'urgent'));
    }
  };

  const updateMessagingServiceSid = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setServiceSid(event?.target?.value);
  };

  const handleSubmit = () => {
    updateToTwilioTemplate(true, serviceSid);
  };
  const headerElement = () => {
    return (
      accountDetails?.name && (
        <Text variant="h1" as="h1">
          {accountDetails?.name}
        </Text>
      )
    );
  };

  return (
    <SectionRenderTemplate header={headerElement()}>
      {accountDetails?.name && (
        <Flex container={{ direction: 'column', itemGap: 2 }} item={{ grow: 1 }}>
          <Grid
            container={{
              cols: 'repeat(3, 1fr)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('connection')}</Label>
            <Flex
              container={{
                direction: 'row',
                itemGap: 1,
                alignItems: 'center',
              }}
            >
              <Text variant="secondary">{translate('Twilio WhatsApp')}</Text>
              <Image alt="WhatsApp" src={Logos.WhatsappLogo} width="25px" height="30px" />
            </Flex>
          </Grid>
          <Grid
            container={{
              cols: 'repeat(3, 1fr)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('phone_number')}</Label>
            <Text variant="secondary">{accountDetails?.phoneNumber}</Text>
          </Grid>
          <Grid
            container={{
              cols: 'repeat(3, 33%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('account_sid')}</Label>
            <Text variant="secondary">{accountDetails?.accountSid}</Text>
          </Grid>
          <Grid
            container={{
              cols: 'repeat(2, 33% 67%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('auth_token')}</Label>
            <Flex container={{ itemGap: 2, alignItems: 'end' }}>
              <Text variant="secondary">{getMaskedToken(accountDetails?.authToken)}</Text>
              <Button style={tokenMaskerStyle} onClick={() => setIsMaskEnabled(!isMaskEnabled)}>
                {translate(isMaskEnabled ? 'show_token' : 'hide_token')}
              </Button>
            </Flex>
          </Grid>
          <Grid
            container={{
              cols: 'repeat(2, 33% 67%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Label>{translate('webhook')}</Label>
            <Text variant="secondary">{process.env.REACT_APP_INCOMING_WEBHOOK}/twilio-whatsapp</Text>
          </Grid>
          {!isTwilioTemplateEnabled && (
            <MenuReplyConfiguration
              source="whatsapp"
              connectionId={connectionId}
              menuReplyConfiguration={menuConfiguration}
              hasTwilioWhatsAppTemplate={isTwilioTemplateEnabled}
              messagingServiceSid={serviceSid}
            />
          )}
          <Flex>
            <span style={{ float: 'left' }}>
              <Checkbox
                label={translate('twilio_whatsapp_content_template')}
                name="twilioContentTemplate"
                checked={isTwilioTemplateEnabled}
                onChange={handleCheckboxClick}
              />
            </span>
            <span>
              <CustomTooltip
                info={translate('content_template_tooltip')}
                tooltipStyle={{ marginLeft: '5px', marginTop: '8px' }}
                iconName="help"
              />
            </span>
          </Flex>
          {isTwilioTemplateEnabled && (
            <Grid
              container={{
                cols: 'repeat(3, 30% 52% 8%)',
                gap: 3,
                alignItems: 'center',
              }}
            >
              <Label>
                {translate('messaging_service_sid')} <span style={{ color: theme.base.colors.red['medium'] }}> *</span>
                <CustomTooltip
                  info={translate('messaging_service_sid_tooltip')}
                  tooltipStyle={{ marginLeft: '5px' }}
                  iconName="help"
                />
              </Label>
              <Flex container={{ direction: 'column', itemGap: 1, alignItems: 'end' }}>
                <Flex style={{ width: '100%' }}>
                  <TextArea
                    name="messagingServiceSid"
                    value={serviceSid}
                    onChange={updateMessagingServiceSid}
                    required={true}
                    autoComplete="off"
                    status={!serviceSid ? 'error' : undefined}
                  />
                  {!serviceSid && <Text status="error">{translate('messaging_service_sid_empty')}</Text>}
                </Flex>
              </Flex>
              <Button
                variant="primary"
                disabled={!serviceSid || serviceSid === messagingServiceSid}
                onClick={handleSubmit}
              >
                {translate('save')}
              </Button>
            </Grid>
          )}
          {configRecords && Object.keys(configRecords).length > 0 && (
            <RealTimeTranslation
              translationEnablerError={translationEnablerError}
              setTranslationEnablerError={setTranslationEnablerError}
              connectionId={connectionId}
              showPrimaryLanguage={true}
              languageTranslationEnabled={accountDetails.languageTranslationEnabled}
              translateFromLanguages={accountDetails.translateFromLanguages}
              translationServiceId={accountDetails.translationServiceId}
              translateToLanguage={accountDetails.primaryLanguage}
            />
          )}
          <Flex style={{ paddingTop: '0.5rem' }}>
            <Button onClick={deleteConfirmation}>{translate('delete_connection_button')}</Button>
          </Flex>
        </Flex>
      )}
    </SectionRenderTemplate>
  );
};

export default TwilioWhatsapp;
